import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ProductCatalogItem } from '@ozon-ai/ozon/contracts';
import type { PurchaseResponse } from '@ozon-ai/procurement/contracts';
import { PURCHASE_STATUS_LABELS } from '@ozon-ai/procurement/contracts';
import { cn } from 'cn';
import { api } from '../api/client';
import {
  gridRowsToLineInputs,
  PurchaseGrid,
  type PurchaseGridHandle,
  purchaseToGridRows,
} from '../components/PurchaseGrid';
import type { PurchaseGridRow } from '../components/OfferIdCellRenderer';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useTelegram } from '../context/TelegramContext';
import { useBreadcrumbPageTitle } from '../routes/breadcrumb-page-title';
import { ContentCard } from '../ui/ContentCard';
import { DateField } from '../ui/DateField';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx';
import { ErrorText, HintText, SuccessText } from '../ui/PageShell';
import { pageContentClassName } from '../ui/pageContentClass';
import { InlineTooltip } from '../ui/InlineTooltip';
import { StatusBadge } from '../ui/StatusBadge';

export function PurchaseDetailPage() {
  const { purchaseId: purchaseIdParam } = useParams();
  const purchaseId = purchaseIdParam ? decodeURIComponent(purchaseIdParam) : '';
  const navigate = useNavigate();
  const { webApp } = useTelegram();

  const [purchase, setPurchase] = useState<PurchaseResponse | null>(null);
  const [catalog, setCatalog] = useState<ProductCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadCatalog = useCallback(async (refresh = false) => {
    try {
      const items = await api.get<ProductCatalogItem[]>(
        `/products/catalog${refresh ? '?refresh=true' : ''}`,
      );
      setCatalog(items);
    } catch {
      setCatalog([]);
    }
  }, []);

  const loadPurchase = useCallback(async () => {
    if (!purchaseId) return;
    setError(null);
    try {
      const data = await api.get<PurchaseResponse>(
        `/purchases/detail?id=${encodeURIComponent(purchaseId)}`,
      );
      setPurchase(data);
    } catch (err) {
      setPurchase(null);
      setError(err instanceof Error ? err.message : 'Не удалось загрузить закупку');
    } finally {
      setLoading(false);
    }
  }, [purchaseId]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    setLoading(true);
    void loadPurchase();
  }, [loadPurchase]);

  if (!purchaseId) {
    return (
      <div className={pageContentClassName}>
        <ErrorText>Не указан идентификатор закупки</ErrorText>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;

  if (!purchase) {
    return (
      <div className={pageContentClassName}>
        <ErrorText>{error ?? 'Закупка не найдена'}</ErrorText>
        <Button type="button" variant="ghost" className="mt-3" onClick={() => navigate('/purchases')}>
          К списку закупок
        </Button>
      </div>
    );
  }

  return (
    <PurchaseDetail
      purchase={purchase}
      catalog={catalog}
      busy={busy}
      error={error}
      onBack={() => navigate('/purchases')}
      onReload={loadPurchase}
      onRefreshCatalog={() => void loadCatalog(true)}
      setBusy={setBusy}
      setError={setError}
      setPurchase={setPurchase}
      webApp={webApp}
    />
  );
}

function PurchaseDetail({
  purchase,
  catalog,
  busy,
  error,
  onBack,
  onReload,
  onRefreshCatalog,
  setBusy,
  setError,
  setPurchase,
  webApp,
}: {
  purchase: PurchaseResponse;
  catalog: ProductCatalogItem[];
  busy: boolean;
  error: string | null;
  onBack: () => void;
  onReload: () => Promise<void>;
  onRefreshCatalog: () => void;
  setBusy: (value: boolean) => void;
  setError: (value: string | null) => void;
  setPurchase: (purchase: PurchaseResponse) => void;
  webApp: ReturnType<typeof useTelegram>['webApp'];
}) {
  const readOnly = purchase.status === 'signed';
  const [deliveryRub, setDeliveryRub] = useState(purchase.deliveryRub);
  const [rows, setRows] = useState<PurchaseGridRow[]>(() => purchaseToGridRows(purchase));
  const [savedPurchase, setSavedPurchase] = useState(purchase);
  const [effectiveFrom, setEffectiveFrom] = useState(purchase.effectiveFrom);
  const purchaseGridRef = useRef<PurchaseGridHandle>(null);
  const { setTitle: setBreadcrumbTitle, setTrailing: setHeaderTrailing } =
    useBreadcrumbPageTitle();
  const purchaseTitle = purchase.title || `Закупка ${purchase.id.slice(0, 6)}`;
  const statusLabel = PURCHASE_STATUS_LABELS[purchase.status] || purchase.status;

  useEffect(() => {
    setDeliveryRub(purchase.deliveryRub);
    setEffectiveFrom(purchase.effectiveFrom);
    setRows(purchaseToGridRows(purchase));
    setSavedPurchase(purchase);
  }, [purchase]);

  const isDirty =
    !readOnly &&
    (Number(deliveryRub) !== savedPurchase.deliveryRub ||
      effectiveFrom !== savedPurchase.effectiveFrom ||
      JSON.stringify(gridRowsToLineInputs(rows)) !==
        JSON.stringify(gridRowsToLineInputs(purchaseToGridRows(savedPurchase))));

  const save = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.put<PurchaseResponse>('/purchases', {
        id: purchase.id,
        deliveryRub: Number(deliveryRub) || 0,
        effectiveFrom,
        lines: gridRowsToLineInputs(rows),
      });
      setSavedPurchase(updated);
      setPurchase(updated);
      setRows(purchaseToGridRows(updated));
      setEffectiveFrom(updated.effectiveFrom);
      webApp?.HapticFeedback?.notificationOccurred('success');
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setBusy(false);
    }
  }, [
    deliveryRub,
    effectiveFrom,
    onReload,
    purchase.id,
    rows,
    setBusy,
    setError,
    setPurchase,
    webApp,
  ]);

  const saveAndSign = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      if (isDirty) {
        const updated = await api.put<PurchaseResponse>('/purchases', {
          id: purchase.id,
          deliveryRub: Number(deliveryRub) || 0,
          effectiveFrom,
          lines: gridRowsToLineInputs(rows),
        });
        setSavedPurchase(updated);
        setPurchase(updated);
        setRows(purchaseToGridRows(updated));
        setEffectiveFrom(updated.effectiveFrom);
      }
      const signed = await api.post<PurchaseResponse>('/purchases/sign', { id: purchase.id });
      setPurchase(signed);
      webApp?.HapticFeedback?.notificationOccurred('success');
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения или подписания');
    } finally {
      setBusy(false);
    }
  }, [
    deliveryRub,
    effectiveFrom,
    isDirty,
    onReload,
    purchase.id,
    rows,
    setBusy,
    setError,
    setPurchase,
    webApp,
  ]);

  async function deactivate() {
    setBusy(true);
    setError(null);
    try {
      await api.delete<PurchaseResponse>(`/purchases?id=${encodeURIComponent(purchase.id)}`);
      webApp?.HapticFeedback?.notificationOccurred('success');
      onBack();
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка удаления');
    } finally {
      setBusy(false);
    }
  }

  const previewTotals = useMemo(() => {
    const totalPurchaseRub = rows.reduce((sum, row) => sum + row.quantity * row.unitPriceRub, 0);
    const delivery = Number(deliveryRub) || 0;
    return {
      totalPurchaseRub: Math.round(totalPurchaseRub * 100) / 100,
      totalWithDeliveryRub: Math.round((totalPurchaseRub + delivery) * 100) / 100,
    };
  }, [rows, deliveryRub]);

  const saveDisabled = busy || !isDirty;
  const saveDisabledReason = saveDisabled
    ? busy
      ? 'Подождите, выполняется операция…'
      : 'Нет изменений для сохранения'
    : null;

  const signDisabled = busy || rows.length === 0;
  const signDisabledReason = signDisabled
    ? busy
      ? 'Подождите, выполняется операция…'
      : 'Добавьте хотя бы одну позицию в таблицу'
    : null;

  const headerTrailing = useMemo((): ReactNode => {
    return (
      <>
        <StatusBadge status={purchase.status}>{statusLabel}</StatusBadge>
        {!readOnly ? (
          <>
            <InlineTooltip message={saveDisabledReason} triggerClassName="inline-flex shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={saveDisabled}
                onClick={() => void save()}
              >
                Сохранить
              </Button>
            </InlineTooltip>
            <InlineTooltip message={signDisabledReason} triggerClassName="inline-flex shrink-0">
              <Button
                type="button"
                size="sm"
                disabled={signDisabled}
                onClick={() => void saveAndSign()}
              >
                Сохранить и подписать
              </Button>
            </InlineTooltip>
          </>
        ) : null}
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          Назад
        </Button>
      </>
    );
  }, [
    onBack,
    purchase.status,
    readOnly,
    save,
    saveAndSign,
    saveDisabled,
    saveDisabledReason,
    signDisabled,
    signDisabledReason,
    statusLabel,
  ]);

  useEffect(() => {
    setBreadcrumbTitle(purchaseTitle);
    setHeaderTrailing(headerTrailing);
    return () => {
      setBreadcrumbTitle(null);
      setHeaderTrailing(null);
    };
  }, [headerTrailing, purchaseTitle, setBreadcrumbTitle, setHeaderTrailing]);

  return (
    <div className={cn(pageContentClassName, 'flex flex-col gap-3 pb-4')}>
      {error ? <ErrorText>{error}</ErrorText> : null}

      <ContentCard contentClassName="pt-4">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="flex flex-col gap-4">
            <DateField
              id="purchaseEffectiveFrom"
              label="Действует с"
              value={effectiveFrom}
              disabled={readOnly}
              onChange={setEffectiveFrom}
            />
            {readOnly ? (
              <HintText>
                Себестоимость позиций учитывается с {formatPurchaseDate(effectiveFrom)}.
              </HintText>
            ) : null}
            <div>
              <Label htmlFor="deliveryRub">Доставка, ₽ (на всю закупку)</Label>
              <Input
                id="deliveryRub"
                type="number"
                inputMode="decimal"
                className="mt-1"
                value={String(deliveryRub)}
                disabled={readOnly}
                onChange={(event) => setDeliveryRub(Number(event.target.value) || 0)}
              />
              <HintText className="mt-1">
                Распределяется пропорционально qty × цена при сохранении.
              </HintText>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <p className="font-heading text-base font-medium">Итого</p>
            <div className="flex flex-col gap-1">
              <MetaRow
                label="Сумма закупки"
                value={`${previewTotals.totalPurchaseRub.toLocaleString('ru-RU')} ₽`}
              />
              <MetaRow
                label="Итого с доставкой"
                value={`${previewTotals.totalWithDeliveryRub.toLocaleString('ru-RU')} ₽`}
                emphasized
              />
            </div>
            {readOnly ? (
              <SuccessText>
                Подписано{' '}
                {purchase.signedAt && new Date(purchase.signedAt).toLocaleString('ru-RU')}
              </SuccessText>
            ) : null}
            {!readOnly && isDirty ? (
              <HintText>Черновик: есть несохранённые изменения</HintText>
            ) : null}
            {purchase.inactive ? (
              <HintText>Закупка помечена как неактивная</HintText>
            ) : (
              <Button
                type="button"
                variant="ghost"
                className="w-full md:w-auto"
                disabled={busy}
                onClick={() => void deactivate()}
              >
                Скрыть (неактивна)
              </Button>
            )}
          </div>
        </div>
      </ContentCard>

      <Card size="sm" className="mb-0 flex min-h-0 flex-col shadow-sm" aria-label="Позиции закупки">
        <CardHeader className="pb-2">
          <CardTitle>Позиции</CardTitle>
          <CardDescription>
            {readOnly
              ? 'Состав закупки: SKU, количество и цены на момент подписания.'
              : 'Добавьте строки и укажите SKU, количество и цену закупки. Для подписания нужна хотя бы одна позиция.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex min-h-[min(50vh,480px)] min-w-0 flex-col p-2 sm:p-3">
          <PurchaseGrid
            ref={purchaseGridRef}
            rows={rows}
            catalog={catalog}
            readOnly={readOnly}
            toolbar="none"
            onRowsChange={setRows}
          />
        </CardContent>
        {!readOnly ? (
          <CardFooter className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => purchaseGridRef.current?.addRow()}
              >
                + Строка
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => purchaseGridRef.current?.removeSelected()}
              >
                Удалить выбранные
              </Button>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={onRefreshCatalog}>
              Обновить каталог
            </Button>
          </CardFooter>
        ) : null}
      </Card>
    </div>
  );
}

function formatPurchaseDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString('ru-RU');
}

function MetaRow({
  label,
  value,
  emphasized,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={emphasized ? 'text-lg font-semibold' : 'text-sm'}>{value}</span>
    </div>
  );
}
